<script setup lang="ts">
import type { ReadonlyDeep } from 'type-fest';
import type { RequiredKeys } from '@/utils/types';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed } from 'vue';
import { formatInTimeZone } from 'date-fns-tz';

import DetailedJobInfoDescListItem from '@/components/items/DetailedJobInfoDescListItem.vue';
import KParentTooltip from '@/components/tooltips/KParentTooltip.vue';
import { useStaticStore } from '@/stores/useStaticStore';
import * as commonConstants from '@/constants/common';

export interface Props {
  stop?: ReadonlyDeep<
    RequiredKeys<
      Partial<
        Pick<
          openapi.JobDespatch['stops'][0],
          | 'address_line_1'
          | 'address_line_2'
          | 'suburb'
          | 'state'
          | 'post_code'
          | 'required_at'
          | 'time_latest'
          | 'services' // TODO DeepPartial for non flat structure?
          | 'attributes'
          | 'driver_is'
          | 'note_name'
          | 'note_phone'
          | 'arrived_at'
          | 'departed_at'
        >
      >
    >
  >;
  loading?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  stop: undefined,
  loading: false,
});

const staticStore = useStaticStore();

function getDriverName(driverCode: number): string | undefined {
  const driver = staticStore.getDrivers.find(
    (driver) => driver.code === driverCode,
  );
  return driver?.full_name;
}

const timeZone = 'Australia/Sydney';

function formatDate(date: string, timeZone: string): string {
  return formatInTimeZone(date, timeZone, 'hh:mm a, MMM d'); // 04:50 PM, Sep 24
}

const stop = computed<Partial<NonNullable<Props['stop']>>>(
  () => props.stop || {},
);
</script>

<!-- TODO similar to @/components/descLists/JobDiagramDescList.vue -->
<template>
  <dl
    :class="$style.dispatchJobDiagramDescList"
    data-testid="dispatchJobDiagramDescList"
  >
    <div :class="$style.wrapper">
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Address">
          {{ stop.address_line_1 }}
          {{ stop.address_line_2
          }}<template v-if="stop.address_line_1 || stop.address_line_2"
            >,</template
          >
          {{ stop.suburb }}<template v-if="stop.suburb">,</template>
          {{ stop.state }}<template v-if="stop.state">,</template>
          {{ stop.post_code }}
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="ETA">
          <template v-if="stop.required_at">
            <span>
              <KParentTooltip>{{ stop.required_at }}</KParentTooltip>
              {{ formatDate(stop.required_at, timeZone) }}
            </span>
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Latest time">
          <template v-if="stop.time_latest">
            <span>
              <KParentTooltip>{{ stop.time_latest }}</KParentTooltip>
              {{ formatDate(stop.time_latest, timeZone) }}
            </span>
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Services">
          <template v-if="stop.services?.length">
            <span
              v-for="(service, serviceIndex) in stop.services"
              :key="serviceIndex"
            >
              <KParentTooltip location="top" :offset="0"
                >Qty {{ service.quantity }}</KParentTooltip
              >
              {{ service.code
              }}<template v-if="serviceIndex < stop.services.length - 1"
                >,</template
              >
            </span>
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Attributes">
          <template v-if="stop.attributes?.length">
            <span
              v-for="(attribute, attributeIndex) in stop.attributes"
              :key="attributeIndex"
            >
              {{ attribute
              }}<template v-if="attributeIndex < stop.attributes.length - 1"
                >,</template
              >
            </span>
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Driver">
          <template v-if="stop.driver_is">
            {{ getDriverName(stop.driver_is) + ' (' + stop.driver_is + ')' }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Contact name">
          <template v-if="stop.note_name">
            {{ stop.note_name }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Contact phone">
          <template v-if="stop.note_phone && stop.note_phone.length > 1">
            <a :href="`callto://${stop.note_phone}`" :class="$style.a">
              <KParentTooltip>Call via Skype</KParentTooltip>
              <v-icon :class="$style.icon" icon="mdi-phone-outline"></v-icon>
              {{ stop.note_phone }}
            </a>
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Arrival time">
          <template v-if="stop.arrived_at">
            <span>
              <KParentTooltip>{{ stop.arrived_at }}</KParentTooltip>
              {{ formatDate(stop.arrived_at, timeZone) }}
            </span>
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Departure time">
          <template v-if="stop.departed_at">
            <span>
              <KParentTooltip>{{ stop.departed_at }}</KParentTooltip>
              {{ formatDate(stop.departed_at, timeZone) }}
            </span>
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
    </div>
  </dl>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.dispatchJobDiagramDescList {
  line-height: 1.4;
  font-size: 16px;
  margin: 0;
}
  .wrapper {
    margin-bottom: -16px;
    columns: 2 160px;
    column-gap: 15px;
  }
    .itemWrapper {
    }
      .item {
        display: inline-block;
        margin: 0 0 16px 0;
      }
        .a {
          text-decoration: none;
        }
          .icon {
            font-size: 17px;
            margin-top: -1px;
          }
</style>
